import React, { memo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box } from "@mui/material";
import { postRequest } from "../../../../../Apis";
import { authEndpoints } from "../../../../../utils/constants/httpConstants";
import EventEmitter from "../../../../../utils/emitter/EventEmitter";
import { EMITTER_SUCCESS, EMITTER_ERROR } from "../../../../../utils/constants";
import {
  BRAND,
  GREY,
  BASE,
  BORDER,
  PRIMARY,
  SUCCESS,
} from "../../../../../utils/constants/colors";
import { fontWeight, ICON_LABELS } from "../../../../../utils/constants/UI";
import IconComponent from "../../../../../components/Icon/IconComponent";
import TypographyInline from "../../../../../components/Typography/TypographyInline";
import { Button } from "../../../../../components/Buttons";
import {
  SDR_INCLUSIONS,
  FEATURE_KEY_LABELS,
} from "./billingUtils";

/**
 * BillingSdrCard
 *
 * Premium "Done for You" card - shows the SDR plan from API data
 * with unlimited features, pricing, and subscribe/talk-to-sales CTAs.
 */
const BillingSdrCard = memo(
  ({ sdrPlan, isCurrent, buyingPlanId, onBuy }) => {
    const price = sdrPlan?.price || 2500;
    const currency = sdrPlan?.currency === "USD" ? "$" : sdrPlan?.currency || "$";
    const interval = sdrPlan?.interval || "year";

    // In-app "Talk to Sales" — one-click lead to hello@reachiq.ai (no meeting link, no form).
    const navigate = useNavigate();
    const [talkBusy, setTalkBusy] = useState(false);
    const [talkDone, setTalkDone] = useState(false);
    const handleTalkToSales = async () => {
      if (talkBusy || talkDone) return;
      setTalkBusy(true);
      try {
        const r = await postRequest(navigate, authEndpoints.sdrLead, {});
        if (r?.data?.status) {
          setTalkDone(true);
          EventEmitter.emit(EMITTER_SUCCESS, r?.data?.message || "Thanks! Our team will reach out to you shortly.");
        } else {
          EventEmitter.emit(EMITTER_ERROR, r?.data?.message || "Could not submit right now. Please try again.");
        }
      } catch (e) {
        EventEmitter.emit(EMITTER_ERROR, e?.data?.message || "Could not submit right now. Please try again.");
      } finally {
        setTalkBusy(false);
      }
    };

    return (
      <Box
        style={{
          width: "100%",
          maxWidth: "720px",
          margin: "0 auto",
          border: isCurrent
            ? `2px solid ${BRAND.PRIMARY}`
            : `1px solid ${BORDER.DEFAULT}`,
          borderRadius: "14px",
          overflow: "hidden",
          background: BASE.WHITE,
          boxShadow: isCurrent
            ? `0 16px 30px -22px ${BRAND.PRIMARY}`
            : `0 14px 26px -24px ${GREY.SECONDARY}`,
          transition:
            "box-shadow 0.18s ease, border-color 0.18s ease, transform 0.18s ease",
        }}
        sx={{
          "&:hover": {
            borderColor: BRAND.PRIMARY,
            boxShadow: `0 18px 30px -24px ${GREY.SECONDARY}`,
            transform: "translateY(-2px)",
          },
        }}
      >
        {/* ── Header ── */}
        <Box
          style={{
            padding: "20px 24px 16px",
            borderBottom: `1px solid ${BORDER.DEFAULT}`,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: "12px",
          }}
        >
          <Box style={{ display: "flex", gap: "14px", minWidth: 0, flex: 1 }}>
            <Box
              style={{
                width: "44px",
                height: "44px",
                borderRadius: "12px",
                background: BRAND.PRIMARY,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <IconComponent
                iconLabel={ICON_LABELS.TRENDING_UP}
                style={{ color: BASE.WHITE, fontSize: "22px" }}
              />
            </Box>
            <Box style={{ minWidth: 0 }}>
              <Box
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  flexWrap: "wrap",
                }}
              >
                <TypographyInline
                  size="lg"
                  fontWeight={fontWeight.BOLD}
                  color={GREY.PRIMARY}
                  label="Managed Services"
                />
                <span
                  style={{
                    fontSize: "10px",
                    fontWeight: 700,
                    background: PRIMARY.W_100,
                    color: BRAND.PRIMARY,
                    border: `1px solid ${PRIMARY.W_200}`,
                    padding: "2px 10px",
                    borderRadius: "999px",
                    letterSpacing: "0.3px",
                  }}
                >
                  DONE FOR YOU
                </span>
              </Box>
              <Box style={{ marginTop: "4px" }}>
                <TypographyInline
                  size="sm"
                  color={GREY.SECONDARY}
                  label="We run your entire outbound. You just show up to meetings."
                />
              </Box>
            </Box>
          </Box>

          {/* Badges */}
          <Box
            style={{
              display: "flex",
              gap: "5px",
              flexWrap: "wrap",
              justifyContent: "flex-end",
              flexShrink: 0,
            }}
          >
            {isCurrent && (
              <span
                style={{
                  padding: "4px 10px",
                  borderRadius: "50px",
                  fontSize: "9px",
                  fontWeight: 700,
                  background: SUCCESS.W_500,
                  color: BASE.WHITE,
                  letterSpacing: "0.2px",
                }}
              >
                ACTIVE
              </span>
            )}
            <span
              style={{
                padding: "4px 10px",
                borderRadius: "50px",
                fontSize: "9px",
                fontWeight: 700,
                background: SUCCESS.W_50,
                color: SUCCESS.W_600,
                letterSpacing: "0.2px",
              }}
            >
              SDR
            </span>
            <span
              style={{
                padding: "4px 10px",
                borderRadius: "50px",
                fontSize: "9px",
                fontWeight: 700,
                background: SUCCESS.W_50,
                color: SUCCESS.W_600,
                letterSpacing: "0.2px",
              }}
            >
              DASH
            </span>
            <span
              style={{
                padding: "4px 10px",
                borderRadius: "50px",
                fontSize: "9px",
                fontWeight: 700,
                background: SUCCESS.W_50,
                color: SUCCESS.W_600,
                letterSpacing: "0.2px",
              }}
            >
              CRM
            </span>
          </Box>
        </Box>

        {/* ── Price + Credits Row ── */}
        <Box
          style={{
            display: "grid",
            gap: "14px",
            padding: "18px 24px",
            background: BASE.BACKGROUND_LIGHT,
            borderBottom: `1px solid ${BORDER.DEFAULT}`,
          }}
          sx={{
            gridTemplateColumns: {
              xs: "1fr",
              sm: "minmax(0, 1fr) auto",
            },
          }}
        >
          <Box
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              minWidth: 0,
            }}
          >
            <TypographyInline
              size="xs"
              fontWeight={fontWeight.BOLD}
              color={GREY.TERTIARY}
              label="PRICE"
            />
            <Box
              style={{
                display: "flex",
                alignItems: "baseline",
                gap: "4px",
                marginTop: "4px",
              }}
            >
              <TypographyInline
                size="dxs"
                fontWeight={fontWeight.BOLD}
                color={GREY.PRIMARY}
                label={`${currency}${price.toLocaleString()}`}
              />
              <TypographyInline
                size="sm"
                color={GREY.SECONDARY}
                fontWeight={fontWeight.MEDIUM}
                label={`/${interval === "year" ? "yr" : "mo"}`}
              />
            </Box>
            <Box style={{ marginTop: "2px" }}>
              <TypographyInline
                size="xs"
                color={GREY.TERTIARY}
                label={
                  interval === "year"
                    ? `Billed annually · ${currency}${Math.round(price / 12).toLocaleString()}/mo`
                    : "Billed monthly"
                }
              />
            </Box>
          </Box>

          <Box
            style={{
              minWidth: "140px",
              padding: "12px 16px",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              border: `1px solid ${BORDER.DEFAULT}`,
              borderRadius: "12px",
              background: BASE.WHITE,
            }}
            sx={{
              alignItems: {
                xs: "flex-start",
                sm: "flex-end",
              },
            }}
          >
            <TypographyInline
              size="xs"
              fontWeight={fontWeight.BOLD}
              color={GREY.TERTIARY}
              label="CREDITS"
            />
            <Box
              style={{
                marginTop: "2px",
                display: "flex",
                alignItems: "center",
                gap: "6px",
              }}
            >
              <IconComponent
                iconLabel={ICON_LABELS.INFINITY}
                style={{ color: BRAND.PRIMARY, fontSize: "20px" }}
              />
              <TypographyInline
                size="dxs"
                fontWeight={fontWeight.BOLD}
                color={BRAND.PRIMARY}
                label="Unlimited"
              />
            </Box>
          </Box>
        </Box>

        {/* ── Features - All Unlimited ── */}
        {sdrPlan?.features &&
          typeof sdrPlan.features === "object" &&
          Object.keys(sdrPlan.features).length > 0 && (
            <Box style={{ padding: "0 24px" }}>
              <Box
                style={{
                  overflow: "hidden",
                  borderRadius: "10px",
                  border: `1px solid ${BORDER.LIGHT}`,
                  background: BASE.WHITE,
                  marginTop: "18px",
                }}
              >
                <Box
                  style={{
                    display: "grid",
                    gridTemplateColumns: "minmax(0, 1fr) 90px 100px",
                    padding: "9px 12px",
                    alignItems: "center",
                    background: BORDER.DISABLED,
                    borderBottom: `1px solid ${BORDER.LIGHT}`,
                  }}
                >
                  <TypographyInline
                    size="xs"
                    fontWeight={fontWeight.BOLD}
                    color={GREY.TERTIARY}
                    label="FEATURE"
                  />
                  <Box style={{ textAlign: "right" }}>
                    <TypographyInline
                      size="xs"
                      fontWeight={fontWeight.BOLD}
                      color={GREY.TERTIARY}
                      label="COST"
                    />
                  </Box>
                  <Box style={{ textAlign: "right" }}>
                    <TypographyInline
                      size="xs"
                      fontWeight={fontWeight.BOLD}
                      color={GREY.TERTIARY}
                      label="LIMIT"
                    />
                  </Box>
                </Box>
                <Box style={{ display: "flex", flexDirection: "column" }}>
                  {Object.entries(sdrPlan.features).map(
                    ([key, value], idx, arr) => (
                      <Box
                        key={key}
                        style={{
                          display: "grid",
                          gridTemplateColumns: "minmax(0, 1fr) 90px 100px",
                          padding: "10px 12px",
                          alignItems: "center",
                          background:
                            idx % 2 === 0 ? BASE.WHITE : BASE.BACKGROUND_LIGHT,
                          borderBottom:
                            idx < arr.length - 1
                              ? `1px solid ${BORDER.LIGHT}`
                              : "none",
                        }}
                      >
                        <Box
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                          }}
                        >
                          <Box
                            style={{
                              width: "6px",
                              height: "6px",
                              borderRadius: "50%",
                              background: BRAND.PRIMARY,
                              flexShrink: 0,
                            }}
                          />
                          <TypographyInline
                            size="xs"
                            fontWeight={fontWeight.MEDIUM}
                            color={GREY.SECONDARY}
                            label={FEATURE_KEY_LABELS[key] || key}
                          />
                        </Box>
                        <Box
                          style={{
                            textAlign: "right",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "flex-end",
                            gap: "4px",
                          }}
                        >
                          <TypographyInline
                            size="xs"
                            fontWeight={fontWeight.BOLD}
                            color={SUCCESS.W_600}
                            label="Free"
                          />
                        </Box>
                        <Box
                          style={{
                            textAlign: "right",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "flex-end",
                            gap: "4px",
                          }}
                        >
                          <IconComponent
                            iconLabel={ICON_LABELS.INFINITY}
                            style={{
                              color: BRAND.PRIMARY,
                              fontSize: "14px",
                            }}
                          />
                          <TypographyInline
                            size="xs"
                            fontWeight={fontWeight.BOLD}
                            color={BRAND.PRIMARY}
                            label="Unlimited"
                          />
                        </Box>
                      </Box>
                    ),
                  )}
                </Box>
              </Box>
            </Box>
          )}

        {/* ── What's Included ── */}
        <Box style={{ padding: "18px 24px 0" }}>
          <TypographyInline
            size="xs"
            fontWeight={fontWeight.BOLD}
            color={GREY.TERTIARY}
            label="WHAT'S INCLUDED"
          />
          <Box
            style={{
              display: "grid",
              gap: "8px",
              marginTop: "10px",
            }}
            sx={{
              gridTemplateColumns: {
                xs: "1fr",
                sm: "1fr 1fr",
              },
            }}
          >
            {SDR_INCLUSIONS.map((item, idx) => (
              <Box
                key={idx}
                style={{
                  display: "flex",
                  gap: "8px",
                  alignItems: "flex-start",
                  padding: "8px 12px",
                  borderRadius: "8px",
                  border: `1px solid ${BORDER.LIGHT}`,
                  background: BASE.BACKGROUND_LIGHT,
                }}
              >
                <Box style={{ flexShrink: 0, marginTop: "2px" }}>
                  <IconComponent
                    iconLabel={ICON_LABELS.CHECK_CIRCLE}
                    style={{ color: SUCCESS.W_500, fontSize: "14px" }}
                  />
                </Box>
                <Box>
                  <span
                    style={{
                      fontSize: "12px",
                      fontWeight: 600,
                      color: GREY.PRIMARY,
                    }}
                  >
                    {item.title}
                  </span>
                  <span style={{ fontSize: "11px", color: GREY.SECONDARY }}>
                    {" · "}
                    {item.desc}
                  </span>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>

        {/* ── Expected Results & Commitment ── */}
        <Box
          style={{
            display: "grid",
            gap: "14px",
            padding: "18px 24px",
          }}
          sx={{
            gridTemplateColumns: {
              xs: "1fr",
              sm: "1fr 1fr",
            },
          }}
        >
          {/* Expected Results */}
          <Box>
            <TypographyInline
              size="xs"
              fontWeight={fontWeight.BOLD}
              color={GREY.TERTIARY}
              label="EXPECTED RESULTS"
            />
            <Box
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "8px",
                marginTop: "8px",
              }}
            >
              <Box
                style={{
                  background: BASE.BACKGROUND_LIGHT,
                  border: `1px solid ${BORDER.LIGHT}`,
                  borderRadius: "10px",
                  padding: "12px",
                  textAlign: "center",
                }}
              >
                <div
                  style={{
                    fontSize: "20px",
                    fontWeight: 800,
                    color: BRAND.PRIMARY,
                    lineHeight: 1.1,
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  5-15
                </div>
                <TypographyInline
                  size="xs"
                  color={GREY.SECONDARY}
                  label="Qualified meetings/mo"
                />
              </Box>
              <Box
                style={{
                  background: BASE.BACKGROUND_LIGHT,
                  border: `1px solid ${BORDER.LIGHT}`,
                  borderRadius: "10px",
                  padding: "12px",
                  textAlign: "center",
                }}
              >
                <div
                  style={{
                    fontSize: "20px",
                    fontWeight: 800,
                    color: BRAND.PRIMARY,
                    lineHeight: 1.1,
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  85%+
                </div>
                <TypographyInline
                  size="xs"
                  color={GREY.SECONDARY}
                  label="Average show rate"
                />
              </Box>
            </Box>
          </Box>

          {/* Commitment */}
          <Box>
            <TypographyInline
              size="xs"
              fontWeight={fontWeight.BOLD}
              color={GREY.TERTIARY}
              label="COMMITMENT"
            />
            <Box
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "8px",
                marginTop: "8px",
              }}
            >
              <Box
                style={{
                  background: BASE.BACKGROUND_LIGHT,
                  border: `1px solid ${BORDER.LIGHT}`,
                  borderRadius: "10px",
                  padding: "12px",
                  textAlign: "center",
                }}
              >
                <div
                  style={{
                    fontSize: "14px",
                    fontWeight: 700,
                    color: BRAND.PRIMARY,
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  3 Months
                </div>
                <TypographyInline
                  size="xs"
                  color={GREY.SECONDARY}
                  label="Minimum pilot"
                />
              </Box>
              <Box
                style={{
                  background: BASE.BACKGROUND_LIGHT,
                  border: `1px solid ${BORDER.LIGHT}`,
                  borderRadius: "10px",
                  padding: "12px",
                  textAlign: "center",
                }}
              >
                <div
                  style={{
                    fontSize: "14px",
                    fontWeight: 700,
                    color: GREY.PRIMARY,
                  }}
                >
                  Month-to-Month
                </div>
                <TypographyInline
                  size="xs"
                  color={GREY.SECONDARY}
                  label="Cancel anytime after pilot"
                />
              </Box>
            </Box>
          </Box>
        </Box>

        {/* ── CTA Footer ── */}
        <Box
          style={{
            padding: "0 24px 22px",
            display: "flex",
            flexDirection: "column",
            gap: "10px",
          }}
        >
          {/* Primary CTA - Subscribe */}
          {sdrPlan && onBuy && (
            <Button
              label={
                buyingPlanId === sdrPlan.id
                  ? "Processing..."
                  : isCurrent
                    ? "Current Plan"
                    : "Subscribe Now"
              }
              size="sm36"
              variant={isCurrent ? "ghost" : "primary"}
              fullWidth
              disabled={isCurrent || buyingPlanId !== null}
              onClick={() => {
                if (!isCurrent) onBuy(sdrPlan);
              }}
            />
          )}

          {/* Secondary CTA - Talk to Sales */}
          <Box
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "6px",
            }}
          >
            <TypographyInline
              size="xs"
              color={GREY.TERTIARY}
              label="Have questions? Pricing can be tailored to your ICP, volume, and goals"
            />
            <Box
              onClick={handleTalkToSales}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  handleTalkToSales();
                }
              }}
              role="button"
              tabIndex={0}
              aria-label="Talk to sales"
              style={{
                width: "100%",
                minHeight: "38px",
                padding: "8px 0",
                borderRadius: "10px",
                background: BASE.WHITE,
                border: `1px solid ${BORDER.DEFAULT}`,
                textAlign: "center",
                cursor: "pointer",
                transition:
                  "border-color 0.18s ease, background 0.18s ease, transform 0.18s ease",
              }}
              sx={{
                "&:hover": {
                  borderColor: BRAND.PRIMARY,
                  background: PRIMARY.W_50,
                  transform: "translateY(-1px)",
                },
                "&:focus-visible": {
                  outline: `2px solid ${BRAND.PRIMARY}`,
                  outlineOffset: "2px",
                },
              }}
            >
              <span
                style={{
                  fontSize: "13px",
                  fontWeight: 600,
                  color: GREY.SECONDARY,
                  letterSpacing: "0.2px",
                }}
              >
                {talkDone ? "Thanks! Our team will reach out." : (talkBusy ? "Sending…" : "Talk to Sales")}
              </span>
            </Box>
          </Box>
        </Box>
      </Box>
    );
  },
);

BillingSdrCard.displayName = "BillingSdrCard";

export default BillingSdrCard;
